import { useState, useEffect } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import "./RegisterStyle.css";

const ResetPassword = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get("token");

  // Form state management
  const [formData, setFormData] = useState({
    password: "",
    confirmPassword: "",
  });

  // UI state management
  const [loading, setLoading] = useState(false);
  const [validating, setValidating] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState("");

  // Validate token on component mount
  useEffect(() => {
    const validateToken = async () => {
      if (!token) {
        setError("Invalid or missing reset token");
        setTokenValid(false);
        setValidating(false);
        return;
      }

      try {
        await axios.get(
          `${process.env.REACT_APP_API_URL || "http://localhost:5000/api"}/auth/validate-reset-token/${token}`
        );
        setTokenValid(true);
      } catch (err) {
        setError(
          err.response?.data?.message ||
          "This reset link is invalid or has expired. Please request a new one."
        );
        setTokenValid(false);
      } finally {
        setValidating(false);
      }
    };

    validateToken();
  }, [token]);

  // Password strength calculator
  const calculatePasswordStrength = (password) => {
    if (!password) return "";

    let strength = 0;
    if (password.length >= 6) strength++;
    if (password.length >= 10) strength++;
    if (/[a-z]/.test(password) && /[A-Z]/.test(password)) strength++;
    if (/\d/.test(password)) strength++;
    if (/[^a-zA-Z0-9]/.test(password)) strength++;

    if (strength <= 2) return "weak";
    if (strength <= 3) return "medium";
    return "strong";
  };

  // Handle input changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));

    // Update password strength on password change
    if (name === "password") {
      setPasswordStrength(calculatePasswordStrength(value));
    }

    // Clear error when user starts typing
    if (error) {
      setError("");
    }
  };

  // Form validation
  const validateForm = () => {
    if (!formData.password) {
      setError("Please enter a new password");
      return false;
    }

    if (formData.password.length < 6) {
      setError("Password must be at least 6 characters long");
      return false;
    }

    if (formData.password !== formData.confirmPassword) {
      setError("Passwords do not match");
      return false;
    }

    return true;
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    // Validate form before submission
    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      await axios.post(
        `${process.env.REACT_APP_API_URL || "http://localhost:5000/api"}/auth/reset-password`,
        {
          token,
          password: formData.password,
        }
      );

      setSuccess(true);

      // Redirect to login after 3 seconds
      setTimeout(() => {
        navigate("/login");
      }, 3000);
    } catch (err) {
      setError(
        err.response?.data?.message ||
        "Failed to reset password. Please try again or request a new reset link."
      );
    } finally {
      setLoading(false);
    }
  };

  // Validating token UI
  if (validating) {
    return (
      <div className="reset-password-container-main">
        <div className="container py-5">
          <div className="row justify-content-center">
            <div className="col-md-6 text-center">
              <div className="reset-password-validating">
                <div className="spinner-border text-light mb-3" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
                <h4 className="text-white">Validating reset link...</h4>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Invalid token UI
  if (!tokenValid) {
    return (
      <div className="reset-password-container-main">
        <div className="container py-5">
          <div className="row justify-content-center">
            <div className="col-md-8 col-lg-6">
              <div className="reset-password-card-shadow reset-password-rounded-modern overflow-hidden bg-white p-5 text-center">
                <div className="reset-password-error-icon-wrapper mb-4">
                  <i className="fa fa-exclamation-triangle reset-password-error-icon"></i>
                </div>
                <h3 className="fw-bold mb-3">Invalid Reset Link</h3>
                <p className="text-muted mb-4">{error}</p>
                <div className="d-grid gap-2">
                  <Link to="/forgot-password" className="btn reset-password-btn-primary">
                    <i className="fa fa-key me-2"></i>
                    Request New Reset Link
                  </Link>
                  <Link to="/login" className="btn reset-password-btn-outline">
                    <i className="fa fa-sign-in me-2"></i>
                    Back to Login
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="reset-password-container-main">
      <div className="container py-5">
        <div className="row justify-content-center">
          <div className="col-md-10 col-lg-8 col-xl-7">
            <div className="row g-0 reset-password-card-shadow reset-password-rounded-modern overflow-hidden reset-password-fade-in">
              {/* Left Side - Form */}
              <div className="col-md-7 bg-white p-5">
                {!success ? (
                  <>
                    <div className="reset-password-form-header mb-4">
                      <h3 className="fw-bold mb-2">
                        <i className="fa fa-lock reset-password-header-icon me-2"></i>
                        Create New Password
                      </h3>
                      <p className="text-muted reset-password-header-subtitle">
                        Choose a strong password to secure your account
                      </p>
                    </div>

                    {/* Error Alert */}
                    {error && (
                      <div className="reset-password-alert-danger mb-3">
                        <i className="fa fa-exclamation-circle me-2"></i>
                        <span>{error}</span>
                      </div>
                    )}

                    <form onSubmit={handleSubmit} noValidate>
                      {/* New Password Field */}
                      <div className="mb-3">
                        <label className="reset-password-form-label fw-semibold">
                          <i className="fa fa-lock text-muted me-2"></i>
                          New Password
                        </label>
                        <div className="reset-password-input-group position-relative">
                          <input
                            type={showPassword ? "text" : "password"}
                            className="form-control reset-password-input-modern"
                            name="password"
                            value={formData.password}
                            onChange={handleChange}
                            placeholder="At least 6 characters"
                            required
                            autoComplete="new-password"
                            autoFocus
                          />
                          <button
                            type="button"
                            className="reset-password-password-toggle"
                            onClick={() => setShowPassword(!showPassword)}
                            tabIndex="-1"
                          >
                            <i className={`fa fa-eye${showPassword ? "-slash" : ""}`}></i>
                          </button>
                        </div>
                        {/* Password Strength Indicator */}
                        {formData.password && (
                          <div className={`reset-password-password-strength ${passwordStrength}`}>
                            <div className="reset-password-strength-bar"></div>
                          </div>
                        )}
                        {passwordStrength && (
                          <small className={`reset-password-strength-text reset-password-strength-${passwordStrength}`}>
                            Password strength: {passwordStrength}
                          </small>
                        )}
                      </div>

                      {/* Confirm Password Field */}
                      <div className="mb-4">
                        <label className="reset-password-form-label fw-semibold">
                          <i className="fa fa-lock text-muted me-2"></i>
                          Confirm Password
                        </label>
                        <div className="reset-password-input-group position-relative">
                          <input
                            type={showConfirmPassword ? "text" : "password"}
                            className="form-control reset-password-input-modern"
                            name="confirmPassword"
                            value={formData.confirmPassword}
                            onChange={handleChange}
                            placeholder="Re-enter your password"
                            required
                            autoComplete="new-password"
                          />
                          <button
                            type="button"
                            className="reset-password-password-toggle"
                            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                            tabIndex="-1"
                          >
                            <i className={`fa fa-eye${showConfirmPassword ? "-slash" : ""}`}></i>
                          </button>
                        </div>
                        {/* Password Match Indicator */}
                        {formData.confirmPassword && formData.password && (
                          <small className={`d-flex align-items-center mt-2 ${
                            formData.password === formData.confirmPassword
                              ? "text-success"
                              : "text-danger"
                          }`}>
                            <i className={`fa fa-${
                              formData.password === formData.confirmPassword
                                ? "check-circle"
                                : "times-circle"
                            } me-1`}></i>
                            {formData.password === formData.confirmPassword
                              ? "Passwords match"
                              : "Passwords do not match"}
                          </small>
                        )}
                      </div>

                      {/* Submit Button */}
                      <button
                        type="submit"
                        className="btn reset-password-btn-primary w-100 mb-3"
                        disabled={loading}
                      >
                        {loading ? (
                          <>
                            <span className="spinner-border spinner-border-sm me-2"></span>
                            Resetting Password...
                          </>
                        ) : (
                          <>
                            <i className="fa fa-check me-2"></i>
                            Reset Password
                          </>
                        )}
                      </button>

                      {/* Back to Login Link */}
                      <div className="text-center reset-password-back-link">
                        <Link to="/login" className="reset-password-link">
                          <i className="fa fa-arrow-left me-1"></i>
                          Back to Login
                        </Link>
                      </div>
                    </form>

                    {/* Password Requirements */}
                    <div className="reset-password-requirements-section mt-4 pt-3 border-top">
                      <h6 className="fw-semibold mb-3">
                        <i className="fa fa-shield me-2"></i>
                        Password Requirements:
                      </h6>
                      <ul className="reset-password-requirements-list small text-muted">
                        <li className={formData.password.length >= 6 ? "text-success" : ""}>
                          <i className={`fa fa-${formData.password.length >= 6 ? "check" : "circle-o"} me-1`}></i>
                          At least 6 characters
                        </li>
                        <li className={/[A-Z]/.test(formData.password) && /[a-z]/.test(formData.password) ? "text-success" : ""}>
                          <i className={`fa fa-${/[A-Z]/.test(formData.password) && /[a-z]/.test(formData.password) ? "check" : "circle-o"} me-1`}></i>
                          Mix of uppercase and lowercase (recommended)
                        </li>
                        <li className={/\d/.test(formData.password) ? "text-success" : ""}>
                          <i className={`fa fa-${/\d/.test(formData.password) ? "check" : "circle-o"} me-1`}></i>
                          At least one number (recommended)
                        </li>
                        <li className={/[^a-zA-Z0-9]/.test(formData.password) ? "text-success" : ""}>
                          <i className={`fa fa-${/[^a-zA-Z0-9]/.test(formData.password) ? "check" : "circle-o"} me-1`}></i>
                          Special character (recommended)
                        </li>
                      </ul>
                    </div>
                  </>
                ) : (
                  <div className="reset-password-success-content text-center">
                    <div className="reset-password-success-icon-wrapper mb-4">
                      <i className="fa fa-check-circle reset-password-success-icon"></i>
                    </div>
                    <h3 className="fw-bold mb-3">Password Reset Successful!</h3>
                    <p className="text-muted mb-4">
                      Your password has been successfully reset. You can now sign in with your new password.
                    </p>
                    <div className="reset-password-redirect-message">
                      <i className="fa fa-spinner fa-spin me-2"></i>
                      Redirecting to login page...
                    </div>
                  </div>
                )}
              </div>

              {/* Right Side - Information */}
              <div className="col-md-5 reset-password-gradient-bg text-white p-5 d-flex flex-column justify-content-center">
                <div>
                  <div className="reset-password-brand-section mb-4">
                    <i className="fa fa-shield reset-password-brand-icon mb-3"></i>
                    <h2 className="fw-bold mb-3 reset-password-brand-title">
                      Secure Your Account
                    </h2>
                    <p className="reset-password-brand-description mb-4">
                      Create a strong password to keep your account safe and secure.
                    </p>
                  </div>

                  {/* Security Tips */}
                  <div className="reset-password-features-list">
                    <h6 className="fw-semibold mb-3">
                      <i className="fa fa-lightbulb-o me-2"></i>
                      Password Tips:
                    </h6>
                    <div className="reset-password-feature-item mb-3">
                      <i className="fa fa-check-circle fa-lg me-3"></i>
                      <span>Use a unique password</span>
                    </div>
                    <div className="reset-password-feature-item mb-3">
                      <i className="fa fa-check-circle fa-lg me-3"></i>
                      <span>Mix letters, numbers & symbols</span>
                    </div>
                    <div className="reset-password-feature-item mb-3">
                      <i className="fa fa-check-circle fa-lg me-3"></i>
                      <span>Avoid personal information</span>
                    </div>
                    <div className="reset-password-feature-item">
                      <i className="fa fa-check-circle fa-lg me-3"></i>
                      <span>Use a password manager</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
